import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { KeyRound, Loader2, Scale } from 'lucide-react';
import { Button, Card, Input } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { activateAccount, validateActivation } from '@/lib/api-client';

// Public, token-gated account activation (P4 Section 2.4). The invited user
// opens https://courtcopilot.com/activate/<token>, sets a password, and is
// logged straight in. First login then triggers the onboarding tour (S4).
export function Activate() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [phase, setPhase] = useState<'checking' | 'ready' | 'invalid'>('checking');
  const [email, setEmail] = useState('');
  const [name, setName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const info = await validateActivation(token);
        if (!live) return;
        setEmail(info.email);
        setName(info.name);
        setPhase('ready');
      } catch {
        if (live) setPhase('invalid');
      }
    })();
    return () => {
      live = false;
    };
  }, [token]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Those passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await activateAccount(token, password);
      // The server set the session cookie. Refresh auth state, then send the
      // new user into the app with the first-run tour flag set.
      await refresh();
      try {
        localStorage.setItem('cc.onboarding.pending', '1');
      } catch {
        // ignore storage errors
      }
      navigate('/hearing', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not activate your account.');
      setSubmitting(false);
    }
  }

  return (
    <main className="grid min-h-screen place-items-center bg-stone-950 px-5 py-10">
      <Card variant="panel" padding="lg" className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br from-amber-500 to-amber-700 shadow-md shadow-amber-900/30">
            <Scale className="h-6 w-6 text-stone-950" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-display text-2xl italic text-stone-100">Court Copilot</h1>
            <p className="mt-1 text-xs uppercase tracking-[0.25em] text-stone-500">
              Set your password
            </p>
          </div>
        </div>

        {phase === 'checking' ? (
          <p className="flex items-center justify-center gap-2 text-sm text-stone-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your invitation
          </p>
        ) : phase === 'invalid' ? (
          <div className="space-y-3 text-center">
            <p className="text-sm text-red-300">
              This invitation link is invalid or has already been used.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
            >
              Go to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <p className="text-center text-sm text-stone-400">
              Welcome{name ? `, ${name}` : ''}. You are activating{' '}
              <span className="text-stone-200">{email}</span>.
            </p>
            <div className="space-y-1">
              <label htmlFor="pw" className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
                New password
              </label>
              <Input
                id="pw"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="pw2" className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
                Confirm password
              </label>
              <Input
                id="pw2"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
            {error ? (
              <p
                role="alert"
                className="rounded-md border border-red-900/40 bg-red-950/30 px-3 py-2 text-xs text-red-300"
              >
                {error}
              </p>
            ) : null}
            <Button
              type="submit"
              variant="primary"
              className="w-full"
              disabled={submitting}
              leftIcon={<KeyRound className="h-4 w-4" />}
            >
              {submitting ? 'Activating' : 'Activate and sign in'}
            </Button>
          </form>
        )}
      </Card>
    </main>
  );
}
